/**
 * Status presentation (ADR-UI-002).
 *
 * Maps THW backend operational state enums to non-colour-safe UI presentation.
 * Every state carries a visible text label (de/en) and a {@link StatusTone};
 * the tone renders a distinct SHAPE glyph in {@link StatusPill}, so meaning is
 * never conveyed by colour alone (WCAG 1.4.1) and remains legible in the
 * `high-contrast` theme.
 *
 * The state CODES below are EXPLICIT from the backend domain enums (M07/M08/
 * M10/M12) and are re-declared here (not imported) to keep this module free of
 * a backend-package dependency. Each map is a `Record<State, …>` over the full
 * union, so adding a backend state is a COMPILE ERROR until presentation is
 * provided — the UI can never silently mis-render a new state.
 *
 * The labels are DISPLAY TRANSLATIONS of the state codes (WS-014 i18n), not an
 * assertion of official THW terminology or abbreviations; they remain
 * `RECOMMENDED` pending M25/WS-014 approval. No transition rule, authority or
 * readiness calculation is invented here — this is presentation only.
 */
import type { StatusTone } from "../ui/status-pill";

export type Locale = "de" | "en";

export interface StatusPresentation {
  readonly tone: StatusTone;
  readonly label: string;
}

interface TonedLabels {
  readonly tone: StatusTone;
  readonly de: string;
  readonly en: string;
}

function present(entry: TonedLabels, locale: Locale): StatusPresentation {
  return { tone: entry.tone, label: locale === "de" ? entry.de : entry.en };
}

/* ------------------------------------------------------------------ M08 mission */

export type MissionState =
  | "DRAFT"
  | "MOBILIZATION"
  | "ACTIVE"
  | "RELEASE"
  | "DEMOBILIZATION"
  | "RETURN"
  | "RESTORATION"
  | "OPERATIONALLY_CLOSED"
  | "ADMINISTRATIVELY_CLOSED";

export const MISSION_STATE_PRESENTATION: Record<MissionState, TonedLabels> = {
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  MOBILIZATION: { tone: "info", de: "Mobilmachung", en: "Mobilization" },
  ACTIVE: { tone: "info", de: "Aktiv", en: "Active" },
  RELEASE: { tone: "warning", de: "Freigabe", en: "Release" },
  DEMOBILIZATION: { tone: "warning", de: "Rückbau", en: "Demobilization" },
  RETURN: { tone: "warning", de: "Rückmarsch", en: "Return" },
  RESTORATION: { tone: "warning", de: "Wiederherstellung", en: "Restoration" },
  OPERATIONALLY_CLOSED: { tone: "neutral", de: "Einsatz beendet", en: "Operationally closed" },
  ADMINISTRATIVELY_CLOSED: { tone: "neutral", de: "Abgeschlossen", en: "Administratively closed" },
};

export function presentMissionState(state: MissionState, locale: Locale): StatusPresentation {
  return present(MISSION_STATE_PRESENTATION[state], locale);
}

/* --------------------------------------------------------------- M08 incident */

export type IncidentState =
  | "DRAFT"
  | "REPORTED"
  | "VALIDATED"
  | "ACTIVE"
  | "CONTAINED"
  | "DEMOBILIZING"
  | "CLOSED"
  | "ARCHIVED"
  | "CANCELLED";

export const INCIDENT_STATE_PRESENTATION: Record<IncidentState, TonedLabels> = {
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  REPORTED: { tone: "info", de: "Gemeldet", en: "Reported" },
  VALIDATED: { tone: "info", de: "Geprüft", en: "Validated" },
  ACTIVE: { tone: "info", de: "Aktiv", en: "Active" },
  CONTAINED: { tone: "success", de: "Unter Kontrolle", en: "Contained" },
  DEMOBILIZING: { tone: "warning", de: "Rückbau", en: "Demobilizing" },
  CLOSED: { tone: "neutral", de: "Abgeschlossen", en: "Closed" },
  ARCHIVED: { tone: "neutral", de: "Archiviert", en: "Archived" },
  CANCELLED: { tone: "neutral", de: "Storniert", en: "Cancelled" },
};

export function presentIncidentState(state: IncidentState, locale: Locale): StatusPresentation {
  return present(INCIDENT_STATE_PRESENTATION[state], locale);
}

/** An incident that is over: it holds no resources and commands no work. */
export const TERMINAL_INCIDENT_STATES: ReadonlySet<string> = new Set([
  "CLOSED",
  "ARCHIVED",
  "CANCELLED",
]);

/**
 * Still being worked, and therefore offerable as an operational context.
 * Every surface that *proposes* an incident — the Active-incidents rail, the
 * context switcher, implicit defaulting after login — filters on this. Terminal
 * incidents stay reachable through an explicit act (a deep link, a restored
 * selection, revealing them in the switcher) so review and reopening still work.
 */
export function isOpenIncidentState(state: string): boolean {
  return !TERMINAL_INCIDENT_STATES.has(state);
}

/**
 * Reported or drafted, but not yet being worked: no command structure, no
 * missions, no resources, nothing for an operational panel to show. Open — an
 * operator may absolutely select one — but never the incident an *implicit*
 * default should land on while a live one exists.
 */
export const PRE_OPERATIONAL_INCIDENT_STATES: ReadonlySet<string> = new Set([
  "DRAFT",
  "REPORTED",
]);

/**
 * Being worked right now (validated, active, contained, demobilizing) — the
 * incident an operator opening Operations means. Distinguishing this from
 * merely open is what stops a fresh intake from outranking the operation in
 * progress; see `features/operations/incident-default-selection.ts`.
 */
export function isOperationallyLiveIncidentState(state: string): boolean {
  return isOpenIncidentState(state) && !PRE_OPERATIONAL_INCIDENT_STATES.has(state);
}

/** Mission is live/running (not draft, not a terminal closed state). */
export const ACTIVE_MISSION_STATES: ReadonlySet<MissionState> = new Set([
  "MOBILIZATION",
  "ACTIVE",
  "RELEASE",
  "DEMOBILIZATION",
  "RETURN",
  "RESTORATION",
]);

/* ------------------------------------------------------- M10 resource request */

export type ResourceRequestState =
  | "DRAFT"
  | "SUBMITTED"
  | "AVAILABILITY_CHECK"
  | "PARTIALLY_AVAILABLE"
  | "AVAILABLE"
  | "NOT_AVAILABLE"
  | "APPROVED"
  | "COMMITTED"
  | "ALLOCATED"
  | "COMPLETED"
  | "CANCELLED";

export const RESOURCE_REQUEST_STATE_PRESENTATION: Record<ResourceRequestState, TonedLabels> = {
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  SUBMITTED: { tone: "info", de: "Eingereicht", en: "Submitted" },
  AVAILABILITY_CHECK: { tone: "info", de: "Verfügbarkeitsprüfung", en: "Availability check" },
  PARTIALLY_AVAILABLE: { tone: "warning", de: "Teilweise verfügbar", en: "Partially available" },
  AVAILABLE: { tone: "success", de: "Verfügbar", en: "Available" },
  NOT_AVAILABLE: { tone: "danger", de: "Nicht verfügbar", en: "Not available" },
  APPROVED: { tone: "info", de: "Genehmigt", en: "Approved" },
  COMMITTED: { tone: "info", de: "Zugesagt", en: "Committed" },
  ALLOCATED: { tone: "success", de: "Zugewiesen", en: "Allocated" },
  COMPLETED: { tone: "neutral", de: "Abgeschlossen", en: "Completed" },
  CANCELLED: { tone: "neutral", de: "Storniert", en: "Cancelled" },
};

export function presentResourceRequestState(
  state: ResourceRequestState,
  locale: Locale,
): StatusPresentation {
  return present(RESOURCE_REQUEST_STATE_PRESENTATION[state], locale);
}

/* ------------------------------------------------------------------ M06 asset */

/**
 * Asset lifecycle status (EXPLICIT — `apps/api/src/modules/m06-assets/domain/assets.ts`
 * `AssetLifecycleStatus`). Tone mapping is RECOMMENDED (design choice, not a THW
 * doctrine): ACTIVE reads as the normal/serviceable state (success), OUT_OF_SERVICE
 * is a temporary caution (warning), RETIRED is a terminal, non-actionable state
 * (neutral) — mirrors the mission/order terminal-state convention above.
 */
export type AssetLifecycleStatus = "ACTIVE" | "OUT_OF_SERVICE" | "RETIRED";

export const ASSET_LIFECYCLE_STATUS_PRESENTATION: Record<AssetLifecycleStatus, TonedLabels> = {
  ACTIVE: { tone: "success", de: "Aktiv", en: "Active" },
  OUT_OF_SERVICE: { tone: "warning", de: "Außer Dienst", en: "Out of service" },
  RETIRED: { tone: "neutral", de: "Ausgemustert", en: "Retired" },
};

export function presentAssetLifecycleStatus(
  status: AssetLifecycleStatus,
  locale: Locale,
): StatusPresentation {
  return present(ASSET_LIFECYCLE_STATUS_PRESENTATION[status], locale);
}

/* ------------------------------------------------------------- M07 readiness */

export type ReadinessStatus =
  | "NOT_EVALUATED"
  | "READY"
  | "READY_WITH_RESTRICTIONS"
  | "NOT_READY"
  | "UNKNOWN_REQUIRES_MANUAL_DECISION";

export const READINESS_STATUS_PRESENTATION: Record<ReadinessStatus, TonedLabels> = {
  NOT_EVALUATED: { tone: "neutral", de: "Nicht bewertet", en: "Not evaluated" },
  READY: { tone: "success", de: "Einsatzbereit", en: "Ready" },
  READY_WITH_RESTRICTIONS: {
    tone: "warning",
    de: "Bereit mit Einschränkungen",
    en: "Ready with restrictions",
  },
  NOT_READY: { tone: "danger", de: "Nicht einsatzbereit", en: "Not ready" },
  UNKNOWN_REQUIRES_MANUAL_DECISION: {
    tone: "warning",
    de: "Unklar – manuelle Entscheidung nötig",
    en: "Unknown – manual decision required",
  },
};

export function presentReadinessStatus(status: ReadinessStatus, locale: Locale): StatusPresentation {
  return present(READINESS_STATUS_PRESENTATION[status], locale);
}

/* ----------------------------------------------------------------- M09 command */

/**
 * M09 command-change-proposal lifecycle (EXPLICIT —
 * `apps/api/src/modules/m09-command/domain/command.ts`
 * `CommandChangeProposalStatus`). `SUBMITTED` awaits an independent reviewer
 * (IMP-010: the submitter may never review their own proposal —
 * `SELF_REVIEW_DENIED` in `command-service.ts`); `REVIEWED` is an approved
 * proposal awaiting a separate, explicit apply step that rechecks current
 * command authority; `APPLIED` is the only state that produced an effective
 * command-relationship version; `REJECTED` is terminal.
 */
export type CommandProposalStatus = "APPLIED" | "REJECTED" | "REVIEWED" | "SUBMITTED";

export const COMMAND_PROPOSAL_STATUS_PRESENTATION: Record<CommandProposalStatus, TonedLabels> = {
  APPLIED: { tone: "success", de: "Angewendet", en: "Applied" },
  REJECTED: { tone: "danger", de: "Abgelehnt", en: "Rejected" },
  REVIEWED: { tone: "info", de: "Geprüft", en: "Reviewed" },
  SUBMITTED: { tone: "warning", de: "Eingereicht", en: "Submitted" },
};

export function presentCommandProposalStatus(
  status: CommandProposalStatus,
  locale: Locale,
): StatusPresentation {
  return present(COMMAND_PROPOSAL_STATUS_PRESENTATION[status], locale);
}

/* ----------------------------------------------------------------- M12 order */

export type OrderStatus = "DRAFT" | "ISSUED" | "COMPLETED" | "CANCELLED";

export const ORDER_STATUS_PRESENTATION: Record<OrderStatus, TonedLabels> = {
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  ISSUED: { tone: "info", de: "Erteilt", en: "Issued" },
  COMPLETED: { tone: "neutral", de: "Abgeschlossen", en: "Completed" },
  CANCELLED: { tone: "neutral", de: "Storniert", en: "Cancelled" },
};

export function presentOrderStatus(status: OrderStatus, locale: Locale): StatusPresentation {
  return present(ORDER_STATUS_PRESENTATION[status], locale);
}

export type RecipientStatus =
  | "PENDING"
  | "DELIVERED"
  | "RECEIVED"
  | "ACKNOWLEDGED"
  | "REJECTED"
  | "UNABLE_TO_EXECUTE";

export const RECIPIENT_STATUS_PRESENTATION: Record<RecipientStatus, TonedLabels> = {
  PENDING: { tone: "warning", de: "Ausstehend", en: "Pending" },
  DELIVERED: { tone: "info", de: "Zugestellt", en: "Delivered" },
  RECEIVED: { tone: "info", de: "Empfangen", en: "Received" },
  ACKNOWLEDGED: { tone: "success", de: "Bestätigt", en: "Acknowledged" },
  REJECTED: { tone: "danger", de: "Abgelehnt", en: "Rejected" },
  UNABLE_TO_EXECUTE: { tone: "danger", de: "Nicht ausführbar", en: "Unable to execute" },
};

export function presentRecipientStatus(status: RecipientStatus, locale: Locale): StatusPresentation {
  return present(RECIPIENT_STATUS_PRESENTATION[status], locale);
}

/**
 * Travel decision (M12). IMP-014 keeps travel authorization at PENDING/UNKNOWN
 * by default; the UI surfaces those states and never resolves them.
 */
export type TravelDecisionStatus =
  | "NOT_REQUIRED"
  | "PENDING"
  | "AUTHORIZED"
  | "INHERITED"
  | "REJECTED"
  | "UNKNOWN";

export const TRAVEL_DECISION_STATUS_PRESENTATION: Record<TravelDecisionStatus, TonedLabels> = {
  NOT_REQUIRED: { tone: "neutral", de: "Nicht erforderlich", en: "Not required" },
  PENDING: { tone: "warning", de: "Ausstehend", en: "Pending" },
  AUTHORIZED: { tone: "success", de: "Genehmigt", en: "Authorized" },
  INHERITED: { tone: "info", de: "Übernommen", en: "Inherited" },
  REJECTED: { tone: "danger", de: "Abgelehnt", en: "Rejected" },
  UNKNOWN: { tone: "warning", de: "Unbekannt", en: "Unknown" },
};

export function presentTravelDecisionStatus(
  status: TravelDecisionStatus,
  locale: Locale,
): StatusPresentation {
  return present(TRAVEL_DECISION_STATUS_PRESENTATION[status], locale);
}

/* ---------------------------------------------- PROVISIONAL (ADR-UI-002 §4) */

/**
 * PROVISIONAL — ASSUMED, stakeholder + backend confirmation required.
 *
 * THW has no per-resource field-deployment state today. This advisory enum is
 * isolated here on purpose (ADR-UI-002 §Decision 4): it is DERIVED from backend
 * allocation/order data where available, shown only as an advisory hint, and
 * NEVER persisted as authoritative THW state or treated as command authority.
 * If a THW deployment-status doctrine is confirmed (or a backend field is
 * added), only this block changes.
 */
export type ProvisionalDeploymentState =
  | "AT_BASE"
  | "NOTIFIED"
  | "EN_ROUTE"
  | "ON_SCENE"
  | "RETURNING";

export const PROVISIONAL_DEPLOYMENT_PRESENTATION: Record<ProvisionalDeploymentState, TonedLabels> = {
  AT_BASE: { tone: "neutral", de: "Am Standort", en: "At base" },
  NOTIFIED: { tone: "info", de: "Alarmiert", en: "Notified" },
  EN_ROUTE: { tone: "info", de: "Auf Anfahrt", en: "En route" },
  ON_SCENE: { tone: "success", de: "Vor Ort", en: "On scene" },
  RETURNING: { tone: "warning", de: "Auf Rückweg", en: "Returning" },
};

export function presentProvisionalDeploymentState(
  state: ProvisionalDeploymentState,
  locale: Locale,
): StatusPresentation {
  return present(PROVISIONAL_DEPLOYMENT_PRESENTATION[state], locale);
}

/* -------------------------------------------------------- M14 situation mapping */

/**
 * M14 map-layer lifecycle (EXPLICIT — `apps/api/src/modules/m14-situation-mapping/domain/situation.ts`
 * `MapLayer.status`). Only two values: a layer is either an unreviewed `DRAFT`
 * or a reviewed, authorized `APPROVED` registered source.
 */
export type MapLayerStatus = "APPROVED" | "DRAFT";

export const MAP_LAYER_STATUS_PRESENTATION: Record<MapLayerStatus, TonedLabels> = {
  APPROVED: { tone: "success", de: "Freigegeben", en: "Approved" },
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
};

export function presentMapLayerStatus(status: MapLayerStatus, locale: Locale): StatusPresentation {
  return present(MAP_LAYER_STATUS_PRESENTATION[status], locale);
}

/**
 * M14 placed-content (drawing/situation-object/operational-overlay/symbol-
 * placement) lifecycle (EXPLICIT — `apps/api/src/modules/m14-situation-mapping/
 * domain/situation.ts` `MapProposal.status`). `PUBLISHED` is the only
 * operator-visible authoritative state on the shared map; `DRAFT` and
 * `APPROVED` are review-in-progress, `REJECTED` is terminal.
 */
export type MapProposalStatus = "APPROVED" | "DRAFT" | "PUBLISHED" | "REJECTED";

export const MAP_PROPOSAL_STATUS_PRESENTATION: Record<MapProposalStatus, TonedLabels> = {
  APPROVED: { tone: "info", de: "Geprüft", en: "Approved" },
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  PUBLISHED: { tone: "success", de: "Veröffentlicht", en: "Published" },
  REJECTED: { tone: "danger", de: "Abgelehnt", en: "Rejected" },
};

export function presentMapProposalStatus(status: MapProposalStatus, locale: Locale): StatusPresentation {
  return present(MAP_PROPOSAL_STATUS_PRESENTATION[status], locale);
}

/* --------------------------------------------------------------- M05 StAN */

/**
 * StAN catalogue version lifecycle (EXPLICIT —
 * `apps/api/src/modules/m05-stan-catalogue/domain/stan.ts` `StanCatalogueStatus`).
 * `PENDING_REVIEW` is a distinct submitted-but-unmodified state.
 * `PENDING_OWNER_APPROVAL` records that independent technical review succeeded
 * while the separate catalogue-owner governance decision is still outstanding.
 * `SCHEDULED` is an approved version whose `effectiveFrom` is still in the future;
 * `SUPERSEDED` is set automatically when a replacement activates; `CONFLICTING`
 * flags a draft/pending-review version whose content contradicts already-
 * approved evidence. A `Record` over the full union — adding a backend state is
 * a compile error here until presentation is provided.
 */
export type StanCatalogueStatus =
  | "ACTIVE"
  | "APPROVED"
  | "CONFLICTING"
  | "DRAFT"
  | "HISTORICAL"
  | "PENDING_OWNER_APPROVAL"
  | "PENDING_REVIEW"
  | "REJECTED"
  | "SCHEDULED"
  | "SUPERSEDED"
  | "WITHDRAWN";

export const STAN_CATALOGUE_STATUS_PRESENTATION: Record<StanCatalogueStatus, TonedLabels> = {
  ACTIVE: { tone: "success", de: "Aktiv", en: "Active" },
  APPROVED: { tone: "info", de: "Genehmigt", en: "Approved" },
  CONFLICTING: { tone: "danger", de: "Widersprüchlich", en: "Conflicting" },
  DRAFT: { tone: "neutral", de: "Entwurf", en: "Draft" },
  HISTORICAL: { tone: "neutral", de: "Historisch", en: "Historical" },
  PENDING_OWNER_APPROVAL: {
    tone: "warning",
    de: "Katalogfreigabe ausstehend",
    en: "Pending catalogue-owner approval",
  },
  PENDING_REVIEW: { tone: "warning", de: "Zur Prüfung", en: "Pending review" },
  REJECTED: { tone: "danger", de: "Abgelehnt", en: "Rejected" },
  SCHEDULED: { tone: "info", de: "Geplant", en: "Scheduled" },
  SUPERSEDED: { tone: "neutral", de: "Abgelöst", en: "Superseded" },
  WITHDRAWN: { tone: "neutral", de: "Zurückgezogen", en: "Withdrawn" },
};

export function presentStanCatalogueStatus(
  status: StanCatalogueStatus,
  locale: Locale,
): StatusPresentation {
  return present(STAN_CATALOGUE_STATUS_PRESENTATION[status], locale);
}

/* ------------------------------------------------------------- data freshness */

/**
 * Data freshness (ADR-UI-002 §5). Freshness is never colour-only: FRESH/STALE/
 * UNKNOWN each carry a text label + a distinct tone/shape. Missing timestamps
 * are UNKNOWN, never assumed fresh.
 */
export type FreshnessLevel = "FRESH" | "STALE" | "UNKNOWN";

export const FRESHNESS_PRESENTATION: Record<FreshnessLevel, TonedLabels> = {
  FRESH: { tone: "success", de: "Aktuell", en: "Fresh" },
  STALE: { tone: "warning", de: "Veraltet", en: "Stale" },
  UNKNOWN: { tone: "neutral", de: "Unbekannt", en: "Unknown" },
};

export function presentFreshness(level: FreshnessLevel, locale: Locale): StatusPresentation {
  return present(FRESHNESS_PRESENTATION[level], locale);
}

/**
 * Classify freshness from a measured timestamp against a staleness threshold.
 * A missing/invalid timestamp is UNKNOWN — never assumed fresh.
 */
export function classifyFreshness(
  measuredAtMs: number | null | undefined,
  nowMs: number,
  staleThresholdMs: number,
): FreshnessLevel {
  if (measuredAtMs === null || measuredAtMs === undefined || !Number.isFinite(measuredAtMs)) {
    return "UNKNOWN";
  }
  return nowMs - measuredAtMs > staleThresholdMs ? "STALE" : "FRESH";
}
